import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { SortEvent, SortEventWithTag } from 'react-sortable-hoc';
import { Dispatch } from 'redux';
import _ from 'lodash';
import arrayMove from 'array-move';
import styled from 'styled-components';
import media from 'styled-media-query';

import { State } from 'src/state/ducks';
import * as bakedBreadActions from 'src/state/ducks/bakedBread/actions';
import * as bakedBreadTagActions from 'src/state/ducks/bakedBreadTag/actions';
import * as shopActions from 'src/state/ducks/shop/actions';
import { BakedBreadEntity } from 'src/firebase/db/BakedBreads';

import StyleConfig from 'src/constants/StyleConfig';
import { DefaultPageProps, HistoryProps } from 'src/constants/Interface';

import { DefaultLayout } from 'src/components/layouts/DefaultLayout';
import Heading from 'src/components/atoms/typo/Heading';
import HomeButton from 'src/components/atoms/button/HomeButton';
import BakedBreadFilterForm from 'src/components/organisms/form/BakedBreadFilterForm';
import BakedBreadList from 'src/components/organisms/list/BakedBreadList';
import SpinnerModal from 'src/components/organisms/loading/SpinnerModal';
import { LinkButton } from 'src/components/atoms/button/Button';
import ErrorMessage from 'src/components/atoms/form/ErrorMessage';

const mapEntityToState = (values: BakedBreadEntity[]) => {
  const bakedBreads = values.map((v, i) => ({
    ...v,
    order: i,
  }));
  return { bakedBreads };
};

const mapStateToEntity = (state: ReturnType<typeof mapEntityToState>) => {
  return state.bakedBreads.map(({ id, order }) => ({ id, order }));
};

type Props = DefaultPageProps &
  HistoryProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const BakedBreadListPage = (props: Props) => {
  const {
    login_shop_id,
    isFetching,
    apiError,
    items,
    bakedBreadTags,
    shop,
    listType,
    getShop,
    getBakedBreads,
    setBakedBreadListType,
    getBakedBreadTags,
    updateBakedBreadOrders,
  } = props;

  const [state, setState] = useState(mapEntityToState(items));
  const [query, setQuery] = useState('');
  // const [listType, setListType] = useState('detail');
  const [bakedBreadTag, setBakedBreadTag] = useState('');
  const { bakedBreads } = state;

  useEffect(() => {
    getShop(login_shop_id);
    getBakedBreads(login_shop_id);
    getBakedBreadTags();
  }, [getShop, getBakedBreads, getBakedBreadTags, login_shop_id]);

  useEffect(() => {
    if (items.length) {
      setState(mapEntityToState(items));
    }
  }, [items]);

  // パンの有効なカテゴリID
  const activeBreadIds = useMemo(
    () =>
      _(items.filter(({ baked_bread_tags: v }) => v && v[0]))
        .map(({ baked_bread_tags }) => baked_bread_tags.map((v) => v.id))
        .flatten()
        .uniq()
        .value(),
    [items],
  );

  const bakedBreadTagOptions = useMemo(() => {
    const tags = bakedBreadTags
      .map(({ id, name }) => ({ value: id, content: name }))
      .filter((v) => activeBreadIds.includes(v.value));
    tags.unshift({ value: '', content: '全て' });
    return tags;
  }, [activeBreadIds, bakedBreadTags]);

  const listTypeOptions = useMemo(
    () => [
      { value: 'detail', content: '詳細表示' },
      { value: 'list', content: 'リスト' },
    ],
    [],
  );

  //
  const onChangeListType = useCallback(
    (type: 'detail' | 'list') => {
      setBakedBreadListType(type);
    },
    [setBakedBreadListType],
  );

  const onChangeBakedBreadTag = useCallback(
    (id: string) => {
      setBakedBreadTag(id);
    },
    [setBakedBreadTag],
  );

  const onChangeQuery = useCallback(
    (text: string) => {
      setQuery(text);
    },
    [setQuery],
  );

  const shouldCancelStart = useCallback(
    (e: SortEvent | SortEventWithTag ) => {
      const touch = e as React.TouchEvent;
      const touches = touch.touches;

      // タッチイベントでない場合はそのまま発火
      if (!touches) {
        return false;
      }

      // 指1本でスワイプされた場合はソートさせない（スクロールできないので）
      if (touches.length === 1) {
        return true;
      }

      // 親要素のliを取得
      const firstChildEl = touches[0].target as HTMLElement;
      const secondChildEl = touches[1].target as HTMLElement;
      const firstParentEl = firstChildEl.closest('li');
      const secondParentEl = secondChildEl.closest('li');

      // 対象の親要素が存在しない場合はソートさせない
      if (!firstParentEl || !secondParentEl) {
        return true;
      }
      
      // 親のliが同一Elementの場合にのみそのまま発火させる
      return !firstParentEl.isSameNode(secondParentEl);
    },
    [],
  );

  // ソート順が変更された時
  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      // ソートに応じてデータを修正
      const sorted = arrayMove(state.bakedBreads, oldIndex, newIndex);

      // ソート順が変わらない場合は何もしない
      if (sorted.every((v, i) => v.order === i)) {
        return;
      }

      const values = sorted.map((v, i) => ({
        ...v,
        order: i,
      }));

      // ソート順が変わっていた時のみ更新する
      const stateValues = { bakedBreads: values };
      const breads = mapStateToEntity(stateValues);

      // stateに適用
      setState(stateValues);

      // データ更新
      updateBakedBreadOrders(breads);
    },
    [state, updateBakedBreadOrders],
  );

  const disabledSort = useMemo(() => query !== '' || bakedBreadTag !== '', [query, bakedBreadTag]);

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <HeadingWrapper>
          <Heading tag="h1" text="編集したいパンを選択してください" />
        </HeadingWrapper>
        {<ErrorMessage fontSize={16}>{apiError?.message}</ErrorMessage>}
        <div className="filter-item">
          <BakedBreadFilterForm
            query={query}
            bakedBreadTag={bakedBreadTag}
            bakedBreadTagOptions={bakedBreadTagOptions}
            listType={listType}
            listTypeOptions={listTypeOptions}
            onChangeQuery={onChangeQuery}
            onChangeBakedBreadTag={onChangeBakedBreadTag}
            onChangeListType={onChangeListType}
          />
          <p>※クリックしたままのドラッグにより商品の並び替えが可能（スマホやタブレットの場合は、指2本でタッチした場合）</p>
          <p>※表示商品を絞り込んでいる状態では並び替えはできません。</p>
        </div>

        <BakedBreadList
          items={bakedBreads}
          shop={shop}
          query={query}
          bakedBreadTag={bakedBreadTag}
          listType={listType}
          shouldCancelStart={shouldCancelStart}
          onSortEnd={onSortEnd}
          disabledSort={disabledSort}
          // lockToContainerEdges={disabledSort}
          distance={1}
          updateBeforeSortStart={(node) => {
            node.node.setAttribute('style', 'z-index:9999;');
          }}
        />
        <ButtonWrapper>
          <LinkButton url={'/baked_breads/create'} text="新しく登録する" isDisabled={false} />
          <HomeButton />
        </ButtonWrapper>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M16} 0;

  .filter-item {
    margin-bottom: 16px;
    p {
      font-size: 14px;
      color: ${StyleConfig.COLOR.TEXT_GRAY};
      ${media.lessThan('medium')`
        padding: 0 24px;
      `};
    }
  }
`;
const HeadingWrapper = styled.div`
  padding: 0 ${StyleConfig.HELPER.M16};
`;
const ButtonWrapper = styled.div`
  padding: 0 ${StyleConfig.HELPER.M16};
  margin-top: 32px;
  > * {
    margin: ${StyleConfig.HELPER.M16} auto;
  }
`;

const mapStateToProps = (state: State) => ({
  items: state.bakedBread.resource,
  bakedBreadTags: state.bakedBreadTag.resource.filter(
    ({ is_disabled, is_featured }) => !is_disabled && is_featured,
  ),
  shop: state.shop.currentShop.resource,
  isFetching:
    state.bakedBread.isFetching || state.shop.isFetching || state.bakedBreadTag.isFetching,
  apiError: state.bakedBread.apiError,
  listType: state.bakedBread.listType,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getShop: (shop_id: string) => dispatch(shopActions.getShop(shop_id)),
  getBakedBreads: (id: string) => dispatch(bakedBreadActions.getBakedBreads(id)),
  setBakedBreadListType: (listType: 'list' | 'detail') => dispatch(bakedBreadActions.setBakedBreadListType(listType)),
  getBakedBreadTags: () => dispatch(bakedBreadTagActions.getBakedBreadTags()),
  updateBakedBreadOrders: (query: { id: string; order: number }[]) =>
    dispatch(bakedBreadActions.updateBakedBreadOrders(query)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BakedBreadListPage);
